import React, { useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import MetaDataBrowser from "app/components/metadatabrowser/metadatabrowser";
import statusManagement from "src/status-management";
import { sxStyles } from "../component-form/sidebar.styles";
import App from "app";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getIsMetadataBrowserOpen } from "../../helpers/selectors";
import { setIsMetadataBrowserOpenAction } from "src/stores/redux/features/flowdesigner/flowdesigner.slice";

type MetadataBrowserProps = {
    app: any;
};

const MetadataBrowserWrapper = ({ app }: MetadataBrowserProps) => {
    const isMetadataBrowserOpen = useAppSelector(getIsMetadataBrowserOpen);
    const dispatch = useAppDispatch();
    const handleCloseMetadataBrowser = useCallback(() => {
        dispatch(setIsMetadataBrowserOpenAction(false));
    }, [dispatch]);
    const containerRef = useRef<HTMLDivElement>(null);
    const metadataBrowserViewRef = useRef<any>(null);

    useEffect(() => {
        if (!app || !containerRef.current) {
            return;
        }

        // Create the MetaDataBrowser view with the same options as in myObjects.js
        const metadataBrowserView = new MetaDataBrowser.View({
            isSmallView: true,
            showDetailofId: true,
            currentAppName: app.name,
            isEditable: statusManagement.isApplicationCreated(app.flowStatus)
        });

        // Apply the same styling as in the original implementation
        metadataBrowserView.$el.removeClass("large").addClass("right-sidebar small sh-lt-4dp");

        // Render the view
        metadataBrowserView.render();
        containerRef.current.replaceChildren(metadataBrowserView.el);

        // Store reference for cleanup
        metadataBrowserViewRef.current = metadataBrowserView;

        // Listen for the close event from the metadata browser
        if (isMetadataBrowserOpen) {
            // Listen to the global App.vent event that the metadata browser triggers
            const handleClose = () => {
                handleCloseMetadataBrowser();
            };

            // Use the imported App object directly
            App.vent.on("metadata-browser:close", handleClose);

            // Cleanup function
            return () => {
                if (metadataBrowserViewRef.current) {
                    metadataBrowserViewRef.current.destroy();
                    metadataBrowserViewRef.current = null;
                }
                App.vent.off("metadata-browser:close", handleClose);
            };
        }

        // Cleanup function
        return () => {
            if (metadataBrowserViewRef.current) {
                metadataBrowserViewRef.current.destroy();
                metadataBrowserViewRef.current = null;
            }
        };
    }, [app?.id, app?.name, app?.flowStatus, isMetadataBrowserOpen]); // Recreate when app changes

    if (!app || !isMetadataBrowserOpen) return null;

    return (
        <Box
            ref={containerRef}
            data-test-id="metadata-browser-container"
            className="sidebar right-sidebar"
            sx={[sxStyles.sidebar("SIDEBAR"), { padding: "0px !important", width: "540px", width: "100% !important" }]}
        />
    );
};

export default MetadataBrowserWrapper;
